import type { Ref } from 'vue';
import { computed } from 'vue';
import { TimeHelper } from '../../legacy/lib/util/time-helper';

type Hint = {
  name: 'hr' | 'min';
  time: number;
};

type Item = {
  id: string;
  name: string;
  time: TimeHelper;
  hint?: Hint;
};

export const useTimePicker = ({ modelValue, minTime }: {
  modelValue: Ref<string>;
  minTime: Ref<string>;
}) => {
  const start = TimeHelper.parse({ time: '00:00:00' });
  const dayMinutes = 24 * 60;
  const timeMinutesPeriod = 15; // интервалы каждые 15 минут
  // End-time options start with fine 15-min steps for the first hour (start + 0, 15,
  // 30, 45) and then fall back to the coarser 30-min step for longer durations.
  const fineStepsCount = 4; // 3 steps of 15 min produce the offsets 0, 15, 30, 45
  const coarseMinutesPeriod = 30;

  const TIME_LIST: Item[] = Array.from({ length: dayMinutes / timeMinutesPeriod }).map((_, index) => {
    const minutes = timeMinutesPeriod * index;
    const time = start.add({ minutes });
    return {
      id: time.toString({
        smallestUnit: 'minute'
      }),
      time,
      name: time.toString({
        smallestUnit: 'minute'
      })
    };
  });

  const parsedValue = computed(() => {
    return modelValue.value ? TimeHelper.parse({ time: modelValue.value }) : null;
  });
  const minParsed = computed(() => {
    return minTime.value ? TimeHelper.parse({ time: minTime.value }) : null;
  });

  const items = computed<Item[]>(() => {
    const min = minParsed.value;
    if (!min) {
      return TIME_LIST;
    }
    // End-time options are anchored to the start time, so they keep its exact minutes
    // (e.g. 17:13 -> 17:13, 17:28, 17:43, 17:58, then 18:28, 18:58, ...). The first hour
    // uses fine 15-min steps (offsets 0, 15, 30, 45); after that the coarser 30-min step.
    const minutesUntilEndOfDay = dayMinutes - (min.hour * 60 + min.minute);
    // `<=` keeps the end-of-day boundary: when an offset lands exactly on midnight
    // (e.g. start 23:45 -> +15 min = 00:00) we still offer it as the last option.
    const offsets: number[] = [];
    for (let offset = 0, step = 0; offset <= minutesUntilEndOfDay; step += 1) {
      offsets.push(offset);
      offset += step < fineStepsCount ? timeMinutesPeriod : coarseMinutesPeriod;
    }

    const minutesInHour = 60;
    return offsets.map((minutes) => {
      const time = min.add({ minutes });
      const name = time.toString({
        smallestUnit: 'minute'
      });

      // Derive the hint straight from the offset instead of `time.since(min)`:
      // `time` wraps around midnight (23:45 + 15 min = 00:00), so `since` would return a
      // negative duration for the end-of-day option. Offsets are already 15-min aligned,
      // so rounding up to the next 15-min step is a no-op kept only for robustness.
      const isHours = minutes >= minutesInHour;
      const roundedMinutes = Math.ceil(minutes / timeMinutesPeriod) * timeMinutesPeriod;
      const count = isHours ? roundedMinutes / minutesInHour : minutes;
      const hint: Hint = isHours
        ? {
            name: 'hr',
            time: count
          }
        : {
            name: 'min',
            time: count
          };
      return {
        id: name,
        name,
        time,
        hint
      };
    });
  });

  const selected = computed(() => {
    if (!parsedValue.value) {
      return undefined;
    }
    return parsedValue.value.toString({
      smallestUnit: 'minute'
    });
  });

  const parseInput = (value: string) => {
    const [hour = '0', minute = '0'] = value.includes(':')
      ? value.split(':')
      : (value.match(/\d{1,2}/g) ?? []);
    const parsedHour = Number(hour);
    const parsedMinute = Number(minute.padEnd(2, '0'));
    const time = TimeHelper.parse({
      hour: parsedHour > 23 ? 0 : parsedHour,
      minute: parsedMinute > 59 ? 0 : parsedMinute
    });
    return time.toString({
      smallestUnit: 'minute'
    });
  };

  return {
    parseInput,
    items,
    selected
  };
};
